import { createHashRouter, RouterProvider, Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, lazy, Suspense } from 'react'
import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { LocalNotifications } from '@capacitor/local-notifications'
import ProtectedRoute from './components/ProtectedRoute'
import Header from './components/Header'
import BottomNav from './components/BottomNav'
import { ErrorFallback } from './components/ErrorBoundary'
import { OnboardingProvider, useOnboarding } from './lib/onboarding'

// Lazy-loaded route components — splits each tab into its own chunk so the
// initial bundle ships only the auth + shell. The retry wrapper auto-reloads
// when a chunk 404s after a new deploy (stale cached HTML referencing old hashes).
function lazyWithRetry<T extends React.ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err)
      // Chrome: "Failed to fetch dynamically imported module".
      // Safari / iOS WKWebView: "Importing a module script failed." or "Load failed".
      if (
        msg.includes('dynamically imported module') ||
        msg.includes('Importing a module script failed') ||
        msg.includes('Failed to fetch') ||
        msg.includes('Load failed')
      ) {
        window.location.reload()
        return new Promise<never>(() => {})
      }
      throw err
    })
  )
}

const Login    = lazyWithRetry(() => import('./pages/Login'))
const Home     = lazyWithRetry(() => import('./pages/Home'))
const History  = lazyWithRetry(() => import('./pages/History'))
const Library  = lazyWithRetry(() => import('./pages/Library'))
const Settings = lazyWithRetry(() => import('./pages/Settings'))
const Terms    = lazyWithRetry(() => import('./pages/Terms'))

function NotificationHandler() {
  const navigate = useNavigate()
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    const handles: { remove: () => Promise<void> }[] = []
    PushNotifications.addListener('pushNotificationActionPerformed', () => {
      navigate('/')
    }).then(h => handles.push(h))
    LocalNotifications.addListener('localNotificationActionPerformed', () => {
      navigate('/')
    }).then(h => handles.push(h))
    return () => { handles.forEach(h => h.remove()) }
  }, [navigate])
  return null
}

function ResetZoom() {
  const { pathname } = useLocation()
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null
    if (!meta) return
    const saved = meta.content
    meta.content = 'width=device-width, initial-scale=1, viewport-fit=cover'
    requestAnimationFrame(() => { meta.content = saved })
  }, [pathname])
  return null
}

function ProtectedLayout({ children }: { children: React.ReactNode }) {
  const { isOnboarding } = useOnboarding()

  return (
    <ProtectedRoute>
      <div className="h-dvh flex flex-col overflow-hidden">
        <Header />
        <main
          className="flex-1 overflow-y-auto"
          style={{
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
            // Must clear the fixed white bar + BottomNav below (height
            // `4rem + safe-area-inset-bottom`), plus a bit extra so a
            // sliver of the page's own background is visible once
            // scrolled all the way down. A plain Tailwind pb-20 (5rem)
            // was shorter than that bar on any phone with a home
            // indicator, so it silently ate itself and the last bit of
            // real content along with it.
            paddingBottom: 'calc(4rem + env(safe-area-inset-bottom) + 1rem)',
          }}
        >
          <Suspense fallback={null}>{children}</Suspense>
        </main>
        {!isOnboarding && (
          <div
            aria-hidden
            className="fixed bottom-0 left-0 right-0 z-30 bg-white pointer-events-none"
            style={{ height: 'calc(4rem + env(safe-area-inset-bottom))' }}
          />
        )}
        {!isOnboarding && <BottomNav />}
      </div>
    </ProtectedRoute>
  )
}

function RootLayout() {
  return (
    <OnboardingProvider>
      <div style={{ minHeight: '100dvh', backgroundImage: 'url(/bg.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <NotificationHandler />
        <ResetZoom />
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
      </div>
    </OnboardingProvider>
  )
}

const router = createHashRouter([
  {
    element: <RootLayout />,
    // Without this, React Router shows its own developer-facing error page
    // for anything thrown during routing or lazy loading.
    errorElement: <ErrorFallback />,
    children: [
      { path: '/login', element: <Login /> },
      { path: '/terms', element: <Terms /> },
      { path: '/', element: <ProtectedLayout><Home /></ProtectedLayout> },
      { path: '/history', element: <ProtectedLayout><History /></ProtectedLayout> },
      { path: '/library', element: <ProtectedLayout><Library /></ProtectedLayout> },
      { path: '/settings', element: <ProtectedLayout><Settings /></ProtectedLayout> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
])

export default function App() {
  return <RouterProvider router={router} />
}
